import { BLOCKS } from '@contentful/rich-text-types';
import {
  useIntl,
  IntlProvider,
  FormattedNumber,
  FormattedNumberParts,
} from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import React from 'react';
import LegalText from '../../../UI/LegalText/LegalText';

import RichText from '../../../UI/RichText';
import Promo from '../../../elements/PromoTag';
import InventoryLogos from '../InventoryBadges/InventoryBadges';
import getPhoneInventoryVisibility from '../../../../utils/phoneStatus/getPhoneInventoryVisibility';

import { InventoryStatus, NearbyStore, ReserveStatus } from '../../../../types';
import { FeaturedPhoneBackImage, InventoryLabels, Offer } from '../../../../ContentfulTypes';
import {
  CarrierOfferForPhone,
  CollapsedContainer,
  TelecomProvider,
  TelecomLogo,
  UpFrontPrice,
  MonthlyPrice,
  Price,
  Disclaimer,
  ButtonGraphicWrapper,
  ButtonGraphic,
  DetailLabel,
  PromoWrapper,
  NoPromo,
  ExpandedTierDetails,
  AccordionWrapper,
  AccordionButton,
} from './styles';

// Section heading rich text options
const tierBodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
  },
};

interface Props {
  offer: Offer;
  labels: Record<string, string>;
  activeStore: NearbyStore;
  inventoryLabels?: InventoryLabels;
  apiError?: boolean;
  inventoryStatus: InventoryStatus;
  selectedCarrier?: string;
  reserveStatus: ReserveStatus;
}

const OfferDetails = ({
  offer,
  labels,
  activeStore,
  inventoryLabels,
  apiError = false,
  inventoryStatus,
  selectedCarrier = '',
  reserveStatus,
}: Props) => {
  const intl = useIntl();

  const renderInventory = getPhoneInventoryVisibility({
    reserveStatus,
    inventoryStatus,
  });

  return (
    <CarrierOfferForPhone uuid={uuidv4()}>
      <AccordionWrapper>
        <AccordionButton>
          <CollapsedContainer>
            {/* LOGO */}
            <TelecomProvider>
              <TelecomLogo image={offer.phoneCarrier?.logo as FeaturedPhoneBackImage} />
            </TelecomProvider>

            {/* UPFRONT PRICE */}
            <UpFrontPrice>
              <Price className="carrierUpFrontPrice">
                <IntlProvider locale={`${intl.locale}-CA`}>
                  {intl.locale === 'en' ? (
                    <FormattedNumber
                      value={offer.carrierUpfrontPrice}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      currency="CAD"
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                    />
                  ) : (
                    <FormattedNumberParts
                      value={offer.carrierUpfrontPrice}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      currency="CAD"
                    >
                      {(parts) => (
                        <>
                          {parts[0].value}
                          {parts[1].value}
                          {parts[2].value}
                          &nbsp;$
                        </>
                      )}
                    </FormattedNumberParts>
                  )}
                </IntlProvider>
                {/* Add asterisks if needed for legal text at bottom of panel */}
                {offer.phoneCarrierTier?.description?.raw?.includes('*')
                  ? '*'
                  : ''}
              </Price>
              <DetailLabel>{labels['6KcxYb1EQRl64daz8jTntc']}</DetailLabel>
            </UpFrontPrice>

            {/* MONTHLY PRICE */}
            <MonthlyPrice>
              <Price className="monthlyPrice">
                <IntlProvider locale={`${intl.locale}-CA`}>
                  {intl.locale === 'en' ? (
                    <FormattedNumber
                      value={offer.monthlyPrice}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      currency="CAD"
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                    />
                  ) : (
                    <FormattedNumberParts
                      value={offer.monthlyPrice}
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      currency="CAD"
                    >
                      {(parts) => (
                        <>
                          {parts[0].value}
                          {parts[1].value}
                          {parts[2].value}
                          &nbsp;$
                        </>
                      )}
                    </FormattedNumberParts>
                  )}
                </IntlProvider>
              </Price>
              <DetailLabel>{labels.aOC9eaI5Lh49zWX8rqb2Y}</DetailLabel>
            </MonthlyPrice>

            {/* PCO POINTS */}
            {offer.pcoPoints > 0 ? (
              <PromoWrapper>
                <Promo
                  isDetailPage
                  bestValueOffer={offer.pcoPoints}
                  asterisk
                />
                <DetailLabel>{labels['2TzLWT8BmcdnnFz6XPkKCA']}</DetailLabel>
              </PromoWrapper>
            ) : (
              <NoPromo />
            )}

            <ButtonGraphicWrapper>
              <ButtonGraphic className="arrowButton" />
            </ButtonGraphicWrapper>
          </CollapsedContainer>
        </AccordionButton>
      </AccordionWrapper>

      {/* TIER DETAILS */}
      <ExpandedTierDetails>
        <Disclaimer>
          <RichText
            document={offer.phoneCarrierTier?.description}
            options={tierBodyOptions}
          />
          {offer.phoneCarrierTier?.legalDetails && (
            <LegalText
              preExpand={false}
              legalHeading={offer.phoneCarrierTier?.legalDetails.heading}
              legalText={[offer.phoneCarrierTier?.legalDetails.contentBody]}
              className="phoneDetailsPage"
            />
          )}
          {renderInventory ? (
            <InventoryLogos
              activeStore={activeStore}
              inventoryLabels={inventoryLabels}
              apiError={apiError}
              inventoryStatus={inventoryStatus}
              selectedCarrier={selectedCarrier}
            />
          ) : (
            ''
          )}
        </Disclaimer>
      </ExpandedTierDetails>
    </CarrierOfferForPhone>
  );
};

export default OfferDetails;
